export type Language = 'en' | 'nl' | 'fr' | 'de' | 'it' | 'es';
export type MeasurementSystem = 'metric' | 'imperial';
export type Theme = 'light' | 'dark' | 'auto';
export type TimeIn3DGraph = 'relative' | 'absolute';
export type ColorPalette = 'default' | 'colorblind_safe' | 'high_contrast';
export type StatusFilter = 'all' | 'live' | 'historical';
export type DateRange = '7d' | '30d' | '90d' | 'all';

export interface Temp3DRange {
  min: number;
  max: number;
}

export interface ProjectListSort {
  column: string;
  direction: 'asc' | 'desc';
}

// ── Effective preferences — the resolved values the UI reads ─────────────────

export interface EffectivePreferences {
  // Localization
  language: Language;
  measurementSystem: MeasurementSystem;
  timeFormat: string;
  dateFormat: string;
  // UI
  theme: Theme;
  rowsPerPage: number;
  // Charts
  temp3DRange: Temp3DRange;
  timeIn3DGraph: TimeIn3DGraph;
  chartsColorPalette: ColorPalette;
  chartsAutoRefreshIntervalS: number;
  // Project list
  projectListVisibleColumns: string[];
  projectListColumnOrder: string[] | null;
  projectListDefaultSort: ProjectListSort;
  projectListDefaultStatusFilter: StatusFilter;
  projectListDefaultDateRange: DateRange;
  // Branding
  brandLogoUrl: string | null;
  splashLogoUrl: string | null;
  reportLogoUrl: string | null;
  primaryColor: string;
  brandingDescription: string;
  // Exports
  wizardTemplateId: string | null;
  exportsDefaultTemplate: string;
  exportsDefaultPdfSections: string[];
  exportReportFooterText: string;
  // Fiber timer (legacy Desktop)
  fiberTimer: number;
  // Notifications
  notificationsEmailEnabled: boolean;
  notificationsRealtimeAlerts: boolean;
  notificationsTelemetryThreshold: number | null;
  notifyOnPreferenceOverwrite: boolean;
  // Security
  securitySessionTimeoutMinutes: number;
  securityRequireMfa: boolean;
  securityAllowedAuthProviders: string[];
  // Cure sheets
  cureEnabledSheetIds: string[];
  cureDefaultSheetId: string | null;
  // Subscriptions
  hybridStreamingEnabled: boolean;
  // Read-only metadata
  companyName: string | null;
  /** Field names locked at company level — inputs for these are disabled for users. */
  lockedFields: string[];
}

// ── Company preferences document (raw from backend) ─────────────────────────

export interface CompanyPreferences {
  // Localization + locks
  defaultLanguage: Language;
  languageLocked: boolean;
  defaultMeasurementSystem: MeasurementSystem;
  measurementSystemLocked: boolean;
  timeFormat: string;
  timeFormatLocked: boolean;
  dateFormat: string;
  dateFormatLocked: boolean;
  // UI
  defaultTheme: Theme;
  // Charts + locks
  default3DTempMin: number;
  default3DTempMax: number;
  temp3DLocked: boolean;
  default3DTimeMode: TimeIn3DGraph;
  defaultChartsColorPalette: ColorPalette;
  chartsColorPaletteLocked: boolean;
  // Project list + locks
  projectListVisibleColumns: string[];
  projectListVisibleColumnsLocked: boolean;
  projectListDefaultSort: ProjectListSort;
  projectListDefaultSortLocked: boolean;
  // Branding
  brandLogoUrl: string | null;
  splashLogoUrl: string | null;
  reportLogoUrl: string | null;
  primaryColor: string;
  brandingDescription: string;
  // Exports
  defaultWizardTemplateId: string | null;
  exportReportFooterText: string;
  // Notifications + locks
  notificationsEmailEnabled: boolean;
  notificationsEmailLocked: boolean;
  notificationsRealtimeAlerts: boolean;
  notificationsRealtimeLocked: boolean;
  notificationsTelemetryThreshold: number | null;
  notificationsTelemetryLocked: boolean;
  // Security
  securitySessionTimeoutMinutes: number;
  securityRequireMfa: boolean;
  securityAllowedAuthProviders: string[];
  // Cure sheets + locks
  cureEnabledSheetIds: string[];
  cureEnabledSheetsLocked: boolean;
  cureDefaultSheetId: string | null;
  cureDefaultSheetLocked: boolean;
  // Subscriptions
  hybridStreamingEnabled: boolean;
  // Legacy
  defaultFiberTimer: number;
}

// ── User preferences document (sparse overrides) ────────────────────────────

export interface UserPreferences {
  // Localization
  language: Language | null;
  measurementSystem: MeasurementSystem | null;
  // UI
  theme: Theme | null;
  rowsPerPage: number | null;
  // Charts
  temp3DRange: Temp3DRange | null;
  timeIn3DGraph: TimeIn3DGraph | null;
  chartsColorPalette: ColorPalette | null;
  chartsAutoRefreshIntervalS: number | null;
  // Project list
  projectListColumnOrder: string[] | null;
  projectListDefaultStatusFilter: StatusFilter | null;
  projectListDefaultDateRange: DateRange | null;
  // Exports
  wizardTemplateId: string | null;
  exportsDefaultTemplate: string | null;
  exportsDefaultPdfSections: string[] | null;
  // Fiber timer
  fiberTimer: number | null;
  // Notifications
  notificationsEmailEnabled: boolean | null;
  notificationsRealtimeAlerts: boolean | null;
  notifyOnPreferenceOverwrite: boolean | null;
  // Cure sheets
  cureDefaultSheetId: string | null;
}

export interface PreferencesApiResponse {
  company: CompanyPreferences;
  user: UserPreferences;
  effective: EffectivePreferences;
  schemaVersion: number;
}

// ── Defaults ─────────────────────────────────────────────────────────────────

export const DEFAULT_EFFECTIVE_PREFERENCES: EffectivePreferences = {
  language: 'en',
  measurementSystem: 'metric',
  timeFormat: 'HH:mm:ss',
  dateFormat: 'yyyy-MM-dd',
  theme: 'dark',
  rowsPerPage: 25,
  temp3DRange: { min: -10, max: 180 },
  timeIn3DGraph: 'relative',
  chartsColorPalette: 'default',
  chartsAutoRefreshIntervalS: 30,
  projectListVisibleColumns: ['ProjectName', 'SerialNumber', 'LastTelemetryReceived', 'FiberType', 'CustomerName'],
  projectListColumnOrder: null,
  projectListDefaultSort: { column: 'LastTelemetryReceived', direction: 'desc' },
  projectListDefaultStatusFilter: 'all',
  projectListDefaultDateRange: 'all',
  brandLogoUrl: null,
  splashLogoUrl: null,
  reportLogoUrl: null,
  primaryColor: '#1a73e8',
  brandingDescription: '',
  wizardTemplateId: null,
  exportsDefaultTemplate: 'Standard',
  exportsDefaultPdfSections: ['Curing', 'Zones', 'UserParameters', 'Graph3D', 'TempOverTime', 'Notes', 'Log'],
  exportReportFooterText: '',
  fiberTimer: 10,
  notificationsEmailEnabled: true,
  notificationsRealtimeAlerts: true,
  notificationsTelemetryThreshold: null,
  notifyOnPreferenceOverwrite: true,
  securitySessionTimeoutMinutes: 60,
  securityRequireMfa: false,
  securityAllowedAuthProviders: ['msal', 'email'],
  cureEnabledSheetIds: [],
  cureDefaultSheetId: null,
  hybridStreamingEnabled: true,
  companyName: null,
  lockedFields: [],
};

export const DEFAULT_COMPANY_PREFERENCES: CompanyPreferences = {
  defaultLanguage: 'en',
  languageLocked: false,
  defaultMeasurementSystem: 'metric',
  measurementSystemLocked: false,
  timeFormat: 'HH:mm:ss',
  timeFormatLocked: false,
  dateFormat: 'yyyy-MM-dd',
  dateFormatLocked: false,
  defaultTheme: 'dark',
  default3DTempMin: -10,
  default3DTempMax: 180,
  temp3DLocked: false,
  default3DTimeMode: 'relative',
  defaultChartsColorPalette: 'default',
  chartsColorPaletteLocked: false,
  projectListVisibleColumns: ['ProjectName', 'SerialNumber', 'LastTelemetryReceived', 'FiberType', 'CustomerName'],
  projectListVisibleColumnsLocked: false,
  projectListDefaultSort: { column: 'LastTelemetryReceived', direction: 'desc' },
  projectListDefaultSortLocked: false,
  brandLogoUrl: null,
  splashLogoUrl: null,
  reportLogoUrl: null,
  primaryColor: '#1a73e8',
  brandingDescription: '',
  defaultWizardTemplateId: null,
  exportReportFooterText: '',
  notificationsEmailEnabled: true,
  notificationsEmailLocked: false,
  notificationsRealtimeAlerts: true,
  notificationsRealtimeLocked: false,
  notificationsTelemetryThreshold: null,
  notificationsTelemetryLocked: false,
  securitySessionTimeoutMinutes: 60,
  securityRequireMfa: false,
  securityAllowedAuthProviders: ['msal', 'email'],
  cureEnabledSheetIds: [],
  cureEnabledSheetsLocked: false,
  cureDefaultSheetId: null,
  cureDefaultSheetLocked: false,
  hybridStreamingEnabled: true,
  defaultFiberTimer: 10,
};
